import type { ReactElement } from 'react';

import {
  BpkAccordion,
  BpkAccordionItem,
  withAccordionItemState,
} from '@skyscanner-internal/backpack-web/bpk-component-accordion';
import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { DangerouslySetInnerHTML } from '@web-platform/shared-universal-features-components/src/DangerouslySetInnerHTML/index';
import { translate } from '@web-platform/shared-universal-translations';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';

const StatefulAccordionItem = withAccordionItemState(BpkAccordionItem);

const getFirstFAQComponentBlock = () => {
  const { market } = getCulture();
  return [
    {
      id: 'how_skyscanner_works',
      question: translate('faqs_how_skyscanner_works_question'),
      answer: translate('faqs_how_skyscanner_works_answer'),
    },
    {
      id: 'find_cheapest_flights',
      question: translate('faqs_find_cheapest_flights_question'),
      answer: (
        <>
          {translate('faqs_find_cheapest_flights_answer')}
          {[
            {
              id: 'faqs_save_money_paragraph',
              header: translate('faqs_save_money_paragraph_heading'),
              text: translate('faqs_save_money_paragraph_text'),
            },
            {
              id: 'faqs_search_everywhere_paragraph',
              header: translate('faqs_search_everywhere_paragraph_heading'),
              text: translate('faqs_search_everywhere_paragraph_text'),
            },
            {
              id: 'faqs_cheapest_time_to_fly_paragraph',
              header: translate('faqs_cheapest_time_to_fly_paragraph_heading'),
              text: translate('faqs_cheapest_time_to_fly_paragraph_text'),
            },
          ].map((section) => (
            <BpkBox marginTop={BpkSpacing.MD} key={section.header}>
              <BpkText textStyle={TEXT_STYLES.bodyDefault} tagName="p">
                {/* BpkBox as="span" is required so the wrapper stays phrasing content
                    inside <p>. display="inline-block" preserves inline flow while
                    allowing margin-inline-end (marginEnd) to apply correctly. */}
                <BpkBox as="span" display="inline-block" marginEnd={BpkSpacing.SM}>
                  <BpkText textStyle={TEXT_STYLES.label1} tagName="span">
                    {section.header}
                  </BpkText>
                </BpkBox>
                <DangerouslySetInnerHTML>{section.text}</DangerouslySetInnerHTML>
              </BpkText>
            </BpkBox>
          ))}
        </>
      ),
    },
    {
      id: 'search_inspiration',
      question: translate('faqs_search_inspiration_question'),
      answer: translate('faqs_search_inspiration_answer', {
        search_everywhere_link: `transport/flights-from/${market}`,
      }),
    },
    {
      id: 'book_flights',
      question: translate('faqs_book_flight_question'),
      answer: translate('faqs_book_flight_answer'),
    },
    {
      id: 'after_booking_flights',
      question: translate('faqs_after_booking_flight_question'),
      answer: translate('faqs_after_booking_flight_answer'),
    },
  ];
};

const getSecondFAQComponentBlock = [
  {
    id: 'hotels',
    question: translate('faqs_hotels_question'),
    answer: translate('faqs_hotels_answer'),
  },
  {
    id: 'car_hire',
    question: translate('faqs_car_hire_question'),
    answer: translate('faqs_car_hire_answer'),
  },
  {
    id: 'price_alert',
    question: translate('faqs_price_alert_question'),
    answer: translate('faqs_price_alert_answer'),
  },
  {
    id: 'flexible_flight',
    question: translate('faqs_flexible_flight_question'),
    answer: translate('faqs_flexible_flight_answer'),
  },
  {
    id: 'low_emission',
    question: translate('faqs_low_emission_question'),
    answer: translate('faqs_low_emission_answer'),
  },
];

const getFAQAnswer = (data: string | JSX.Element) =>
  typeof data === 'string' ? <DangerouslySetInnerHTML>{data}</DangerouslySetInnerHTML> : data;

export const FAQs = (): ReactElement => (
  <BpkBox marginBottom={BpkSpacing.XL} data-testid="faq_component">
    <BpkBox paddingBottom={BpkSpacing.MD}>
      <BpkText tagName="h2" textStyle={TEXT_STYLES.heading2}>
        {translate('faqs_heading')}
      </BpkText>
    </BpkBox>
    <BpkFlex
      direction={{ base: 'column', desktop: 'row' }}
      gap={{ base: BpkSpacing.None, desktop: BpkSpacing.Base }}
    >
      <BpkBox width="100%">
        <BpkAccordion data-testid="faq_section_1">
          {getFirstFAQComponentBlock().map((faq) => (
            <StatefulAccordionItem
              data-testid={faq.id}
              id={faq.id}
              key={faq.id}
              tagName="h3"
              textStyle={TEXT_STYLES.heading5}
              title={faq.question}
            >
              {getFAQAnswer(faq.answer)}
            </StatefulAccordionItem>
          ))}
        </BpkAccordion>
      </BpkBox>
      <BpkBox width="100%">
        <BpkAccordion data-testid="faq_section_2">
          {getSecondFAQComponentBlock.map((faq) => (
            <StatefulAccordionItem
              data-testid={faq.id}
              id={faq.id}
              key={faq.id}
              tagName="h3"
              textStyle={TEXT_STYLES.heading5}
              title={faq.question}
            >
              {getFAQAnswer(faq.answer)}
            </StatefulAccordionItem>
          ))}
        </BpkAccordion>
      </BpkBox>
    </BpkFlex>
  </BpkBox>
);
